import {
  FC,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState
} from 'react';
import { Control, Controller } from 'react-hook-form';

import {
  CodeDashContainer,
  DashDivider,
  VerificationCodeInputContainer,
  VerificationInput
} from './verification-code-input-styles';

type InputOrNull = HTMLInputElement | null;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  length?: number;
}

const VerificationCodeInput: FC<Props> = ({ control, length = 6 }) => {
  const [code, setCode] = useState<string[]>(Array(length).fill(''));
  const update = useCallback((index: number, val: string) => {
    return setCode((prevState) => {
      const slice = prevState.slice();
      slice[index] = val;
      return slice;
    });
  }, []);

  const formRef = useRef<HTMLFormElement>(null);

  function handleKeyDown(evt: KeyboardEvent<HTMLInputElement>) {
    const index = parseInt(evt.currentTarget.dataset.index as string);
    const form = formRef.current;
    if (isNaN(index) || form === null) return; // just in case

    const prevIndex = index - 1;
    const nextIndex = index + 1;
    const prevInput: InputOrNull = form.querySelector(`.input-${prevIndex}`);
    const nextInput: InputOrNull = form.querySelector(`.input-${nextIndex}`);
    switch (evt.key) {
      case 'Backspace':
        if (code[index]) update(index, '');
        else if (prevInput) prevInput.select();
        break;
      case 'ArrowRight':
        evt.preventDefault();
        if (nextInput) nextInput.focus();
        break;
      case 'ArrowLeft':
        evt.preventDefault();
        if (prevInput) prevInput.focus();
        break;
      case 'e':
        evt.preventDefault();
        break;
      case 'E':
        evt.preventDefault();
        break;
      case '+':
        evt.preventDefault();
        break;
      case '-':
        evt.preventDefault();
        break;
    }
  }

  function handleChange(evt: FormEvent<HTMLInputElement>) {
    const value = evt.currentTarget.value;
    const index = parseInt(evt.currentTarget.dataset.index as string);
    const form = formRef.current;
    if (isNaN(index) || form === null) return; // just in case

    let nextIndex = index + 1;
    let nextInput: InputOrNull = form.querySelector(`.input-${nextIndex}`);

    update(index, value[0] || '');
    if (value.length === 1) nextInput?.focus();
    else if (index < length - 1) {
      const split = value.slice(index + 1, length).split('');
      split.forEach((val) => {
        update(nextIndex, val);
        nextInput?.focus();
        nextIndex++;
        nextInput = form.querySelector(`.input-${nextIndex}`);
      });
    }
  }

  function handleFocus(evt: FocusEvent<HTMLInputElement>) {
    evt.currentTarget.select();
  }

  return (
    <VerificationCodeInputContainer ref={formRef}>
      {code.map((value, i) => (
        <CodeDashContainer key={i}>
          {i === length / 2 && <DashDivider />}
          <Controller
            key={i}
            name={`code[${i}]`}
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <VerificationInput
                {...field}
                key={i}
                variant='filled'
                value={value}
                inputProps={{
                  type: i === length - 1 ? 'text' : 'number',
                  className: `input-${i}`,
                  'aria-label': `Number ${i + 1}`,
                  'data-index': i,
                  pattern: '[0-9]*',
                  inputtype: 'numeric',
                  onChange: handleChange,
                  onKeyDown: handleKeyDown,
                  onFocus: handleFocus,
                  maxLength: 1
                }}
              />
            )}
          />
        </CodeDashContainer>
      ))}
    </VerificationCodeInputContainer>
  );
};

export default VerificationCodeInput;
